/**
 * One document opened: header with its facts, the file shown in place (PDF, picture, video, audio, plain text),
 * a file card (with FusionDocs' thumbnail when there is one) and Download for anything else, and older versions.
 * Rename in place and Move to a folder. Full screen with a back button on narrow screens.
 */
import { ShareButton } from "./Share";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { ArrowLeft, Check, Download, FileText, FolderInput, History, Loader2, Pencil, SquareArrowOutUpRight, X } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { ErrorNote } from "../ui";
import { DocFacts } from "./DocMeta";
import { MoveDialog } from "./FolderPicker";
import { afterEdit, contentUrl, day, editErrText, FK, humanSize, patchDoc, previewKind, thumbUrl, type FdDetail } from "./fd";

export function DocPreview({ id, onClose, fullScreen }: { id: string; onClose: () => void; fullScreen: boolean }) {
  const q = useQuery({ queryKey: FK.doc(id), queryFn: () => api<FdDetail>(`/api/fd/documents/${encodeURIComponent(id)}`), retry: false });
  const [version, setVersion] = useState<number | undefined>(undefined);
  const doc = q.data?.document;
  const versions = q.data?.versions ?? [];
  const current = doc?.currentVersion?.versionNo;
  const picked = version != null && version !== current ? versions.find((v) => v.versionNo === version) : undefined;
  const mime = picked?.mimeType ?? doc?.currentVersion?.mimeType ?? versions.find((v) => v.versionNo === current)?.mimeType;
  const ver = picked ? picked.versionNo : undefined;
  const size = picked?.sizeBytes ?? doc?.currentVersion?.sizeBytes;
  const raw = previewKind(mime);
  // plain text is read and shown as text (no frame); very large text files get the file card
  const kind = raw === "text" && (size ?? 0) > 2_000_000 ? null : raw;
  const src = contentUrl(id, { version: ver });
  const text = useQuery({
    queryKey: ["fd", "text", id, ver ?? 0],
    queryFn: async () => {
      const r = await fetch(src, { credentials: "include" });
      if (!r.ok) throw new Error("Couldn't load the text");
      return r.text();
    },
    enabled: kind === "text",
    retry: false,
  });
  const download = contentUrl(id, { download: true, version: ver });
  const title = doc?.title || "Document";
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [moving, setMoving] = useState(false);
  const [thumbBad, setThumbBad] = useState(false);
  const rename = useMutation({
    mutationFn: (t: string) => patchDoc(id, { title: t }),
    onSuccess: (r) => { afterEdit(qc, id, r.document); setEditing(false); },
  });
  const startRename = () => { setDraft(doc?.title ?? ""); rename.reset(); setEditing(true); };
  const saveRename = () => {
    const t = draft.trim();
    if (!t || t === doc?.title) { setEditing(false); return; }
    rename.mutate(t);
  };
  const icon = "btn btn-ghost btn-icon !h-9 !min-h-0 !w-9 shrink-0";

  return (
    <section
      aria-label={`Preview: ${title}`}
      className={clsx(
        "flex min-h-0 flex-col bg-[var(--panel)]",
        fullScreen ? "fixed inset-0 z-50 pb-[env(safe-area-inset-bottom)]" : "panel h-full overflow-hidden",
      )}
    >
      <header className="flex items-start gap-2 border-b border-[var(--line)] px-3 py-3 sm:px-4">
        {fullScreen && (
          <button className="btn btn-ghost btn-icon !h-9 !min-h-0 !w-9 shrink-0" onClick={onClose} aria-label="Back to documents"><ArrowLeft size={18} /></button>
        )}
        {editing ? (
          <form className="min-w-0 flex-1" onSubmit={(e) => { e.preventDefault(); saveRename(); }}>
            <div className="flex items-center gap-1">
              <input
                className="input !min-h-0 min-w-0 flex-1 !py-1.5 text-sm font-semibold"
                value={draft}
                maxLength={300}
                autoFocus
                aria-label="Document title"
                disabled={rename.isPending}
                onChange={(e) => { setDraft(e.target.value); rename.reset(); }}
                onKeyDown={(e) => e.key === "Escape" && !rename.isPending && setEditing(false)}
              />
              <button type="submit" className={icon} disabled={rename.isPending || !draft.trim()} aria-label="Save title" title="Save">
                {rename.isPending ? <Loader2 size={16} className="animate-spin" /> : <Check size={17} />}
              </button>
              <button type="button" className={icon} disabled={rename.isPending} onClick={() => setEditing(false)} aria-label="Cancel rename" title="Cancel"><X size={17} /></button>
            </div>
            {rename.error && <p role="alert" className="mt-1 text-xs text-red-700">{editErrText(rename.error)}</p>}
          </form>
        ) : (
          <div className="min-w-0 flex-1 pt-0.5">
            <h2 className="truncate text-[0.9375rem] font-bold" title={title}>{q.isLoading ? "Loading…" : title}</h2>
            {doc && <DocFacts doc={{ ...doc, currentVersion: picked ? { ...doc.currentVersion, sizeBytes: picked.sizeBytes } : doc.currentVersion }} className="mt-1" />}
          </div>
        )}
        {doc && !editing && (
          <>
            <button className={icon} onClick={startRename} aria-label="Rename" title="Rename"><Pencil size={16} /></button>
            <button className={icon} onClick={() => setMoving(true)} aria-label="Move to a folder" title="Move to a folder"><FolderInput size={17} /></button>
            <ShareButton target={{ kind: "document", id, title: doc.title }} className={icon} />
          </>
        )}
        {doc && kind && !editing && (
          <a href={src} target="_blank" rel="noreferrer" className="btn btn-ghost btn-icon !h-9 !min-h-0 !w-9 shrink-0" aria-label="Open in a new tab" title="Open in a new tab"><SquareArrowOutUpRight size={16} /></a>
        )}
        {doc && !editing && (
          <a href={download} className="btn btn-ghost btn-icon !h-9 !min-h-0 !w-9 shrink-0" aria-label="Download" title="Download"><Download size={17} /></a>
        )}
        {!fullScreen && !editing && (
          <button className="btn btn-ghost btn-icon !h-9 !min-h-0 !w-9 shrink-0" onClick={onClose} aria-label="Close preview"><X size={17} /></button>
        )}
      </header>

      {q.error ? (
        <div className="p-4"><ErrorNote error={q.error} /></div>
      ) : q.isLoading || !doc ? (
        <div className="muted flex flex-1 items-center justify-center gap-2 p-6 text-sm"><Loader2 size={15} className="animate-spin" /> Opening…</div>
      ) : (
        <>
          <div className="relative min-h-[50vh] flex-1 bg-[var(--panel-2)] lg:min-h-0">
            {kind === "pdf" && <iframe key={src} src={src} title={title} className="absolute inset-0 h-full w-full border-0" />}
            {kind === "text" && (
              <div className="scroll-thin absolute inset-0 overflow-auto bg-white p-4">
                {text.error ? <ErrorNote error={text.error} />
                  : text.isLoading ? <p className="muted flex items-center gap-2 text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</p>
                  : <pre className="whitespace-pre-wrap break-words font-mono text-[0.8125rem] leading-relaxed text-[var(--text)]">{text.data}</pre>}
              </div>
            )}
            {kind === "image" && (
              <div className="absolute inset-0 flex items-center justify-center overflow-auto p-4">
                <img key={src} src={src} alt={title} className="max-h-full max-w-full object-contain" />
              </div>
            )}
            {kind === "video" && (
              <div className="absolute inset-0 flex items-center justify-center bg-black p-2">
                <video key={src} src={src} controls playsInline className="max-h-full max-w-full" aria-label={title} />
              </div>
            )}
            {kind === "audio" && (
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <audio key={src} src={src} controls className="w-full max-w-md" aria-label={title} />
              </div>
            )}
            {!kind && (
              <div className="absolute inset-0 flex items-center justify-center p-6">
                <div className="panel flex w-full max-w-sm flex-col items-center gap-3 p-6 text-center">
                  {thumbBad
                    ? <span className="grid h-12 w-12 place-items-center rounded-xl bg-brand-50 text-brand-600"><FileText size={22} aria-hidden /></span>
                    : <img src={thumbUrl(id)} alt="" className="max-h-56 max-w-full rounded-lg border border-[var(--line)] object-contain" onError={() => setThumbBad(true)} />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{title}</p>
                    <p className="muted mt-0.5 text-xs">{[mime, humanSize(size)].filter(Boolean).join(" · ") || "No preview for this kind of file"}</p>
                  </div>
                  <a href={download} className="btn btn-primary btn-sm"><Download size={14} /> Download</a>
                </div>
              </div>
            )}
          </div>

          {(versions.length > 1 || doc.filingPath || doc.lifecycleState || doc.source?.label) && (
            <footer className="space-y-2 border-t border-[var(--line)] px-4 py-3 text-xs">
              {versions.length > 1 && (
                <label className="flex items-center gap-2">
                  <History size={13} className="muted" aria-hidden />
                  <span className="muted">Version</span>
                  <select className="input !min-h-0 !py-1 text-xs" value={ver ?? current ?? ""} onChange={(e) => setVersion(Number(e.target.value))} aria-label="Version to show">
                    {[...versions].sort((a, b) => b.versionNo - a.versionNo).map((v) => (
                      <option key={v.id} value={v.versionNo}>
                        {`v${v.versionNo}${v.versionNo === current ? " (current)" : ""}${v.createdAt ? ` · ${day(v.createdAt)}` : ""}${v.sizeBytes ? ` · ${humanSize(v.sizeBytes)}` : ""}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <p className="muted flex flex-wrap items-center gap-x-2 gap-y-1">
                {doc.filingPath && <span className="truncate">{doc.filingPath}</span>}
                {doc.lifecycleState && <span className="chip !text-[0.7rem] capitalize">{doc.lifecycleState.replace(/_/g, " ")}</span>}
                {doc.source?.label && <span>{doc.source.label}</span>}
              </p>
            </footer>
          )}
        </>
      )}
      {moving && doc && <MoveDialog id={id} title={title} current={doc.filingPath} onClose={() => setMoving(false)} />}
    </section>
  );
}
